import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getCreditsStatus, type CreditsStatus } from "@/lib/api-client";

interface CreditsContextType {
  balance: number;
  actions: Record<string, { credit_cost: number }>;
  canAfford: (actionCode: string) => boolean;
  costOf: (actionCode: string) => number;
  refreshCredits: () => Promise<void>;
  isLoading: boolean;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { profile, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState(0);
  const [actions, setActions] = useState<
    Record<string, { credit_cost: number }>
  >({});
  const [isLoading, setIsLoading] = useState(true);

  const refreshCredits = useCallback(async () => {
    if (!isAuthenticated || !profile?.company_id) {
      setBalance(0);
      setActions({});
      setIsLoading(false);
      return;
    }
    try {
      const status = await getCreditsStatus();
      setBalance(status.balance);
      setActions(status.actions);
    } catch (err) {
      console.warn("Failed to fetch credits status:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, profile?.company_id]);

  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);

  const canAfford = useCallback(
    (actionCode: string) => {
      const cost = actions[actionCode]?.credit_cost ?? 0;
      return cost === 0 || balance >= cost;
    },
    [balance, actions],
  );

  const costOf = useCallback(
    (actionCode: string) => actions[actionCode]?.credit_cost ?? 0,
    [actions],
  );

  return (
    <CreditsContext.Provider
      value={{ balance, actions, canAfford, costOf, refreshCredits, isLoading }}
    >
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error("useCredits must be used within a CreditsProvider");
  return ctx;
}
